// ============================================================
//  MASAMADREAM — Store (LocalStorage)
//  Gestión de estado persistente
// ============================================================

const STORAGE_KEY = 'masagotchi_state';

const DEFAULT_STATE = {
  processes:    [],   // array de procesos activos
  viewingId:    null, // id del proceso actualmente visible
  history:      [],
  starterName:  '',
  achievements: [],
};

export class Store {
  constructor() {
    this._state = this._read();
  }

  // ── Hydrate from localStorage ─────────────────────────────
  _read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return structuredClone(DEFAULT_STATE);
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_STATE, ...parsed };
    } catch {
      return structuredClone(DEFAULT_STATE);
    }
  }

  // ── Persist to localStorage ───────────────────────────────
  _write() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._state));
    } catch (e) {
      console.warn('[MM] Storage write failed:', e);
    }
  }

  // ── Public API ────────────────────────────────────────────

  load() {
    this._state = this._read();
    
    // Migration: backward-compat - if old activeProcess exists, migrate to processes[]
    if (this._state.activeProcess && !this._state.processes) {
      const old = this._state.activeProcess;
      const label = old.type === 'p1' ? 'Fermentación #1' : 'Preparación #1';
      this._state.processes = [{
        id: 'proc_' + Date.now(),
        type: old.type,
        label,
        startDate: old.startDate,
        currentDay: this._calcCurrentDay(old.startDate, old.type),
        completedIds: old.completedIds || [],
        rating: old.rating || null,
      }];
      this._state.viewingId = this._state.processes[0].id;
      delete this._state.activeProcess;
      this._write();
    }
    
    // Recalculate currentDay from startDate for ALL active processes
    if (this._state.processes && this._state.processes.length) {
      this._state.processes.forEach(p => {
        p.currentDay = this._calcCurrentDay(p.startDate, p.type);
      });
    }
    return this._state;
  }

  get state() {
    return this._state;
  }

  getProcess(id) {
    return this._state.processes?.find(p => p.id === id) || null;
  }

  getViewingProcess() {
    if (!this._state.viewingId) return null;
    return this.getProcess(this._state.viewingId);
  }

  setViewingId(id) {
    this._state.viewingId = id;
    this._write();
  }

  getActiveProcesses() {
    return this._state.processes || [];
  }

  startProcess(type) {
    const processes = this._state.processes || [];
    
    // Count existing processes of this type for auto-numbering
    const count = processes.filter(p => p.type === type).length + 1;
    const typeLabels = { p1: 'Fermentación', p2: 'Preparación', p3: 'Pizza' };
    const label = `${typeLabels[type] ?? type.toUpperCase()} #${count}`;
    
    const process = {
      id: 'proc_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      type,
      label,
      startDate:    new Date().toISOString(),
      currentDay:   1,
      completedIds: [],
      rating:       null,
    };

    this._state.processes = processes;
    this._state.processes.push(process);
    this._state.viewingId = process.id;
    this._write();
    return process;
  }

  // Mark an activity as completed
  completeActivity(activityId, processId = this._state.viewingId) {
    const process = this.getProcess(processId);
    if (!process) return;
    const ids = process.completedIds;
    if (!ids.includes(activityId)) {
      ids.push(activityId);
      this._write();
    }
  }

  uncompleteActivity(activityId, processId = this._state.viewingId) {
    const process = this.getProcess(processId);
    if (!process) return;
    const ids = process.completedIds;
    const idx = ids.indexOf(activityId);
    if (idx !== -1) {
      ids.splice(idx, 1);
      this._write();
    }
  }

  isCompleted(activityId, processId = this._state.viewingId) {
    const process = this.getProcess(processId);
    return process?.completedIds?.includes(activityId) ?? false;
  }

  // Archive the current process
  archiveProcess(processId, outcome = 'completed', rating = null) {
    const process = this.getProcess(processId);
    if (!process) return;
    const archived = {
      ...process,
      endDate:  new Date().toISOString(),
      outcome,
      rating,
    };
    this._state.history.push(archived);
    this._state.processes = this._state.processes.filter(p => p.id !== processId);
    if (this._state.viewingId === processId) {
      this._state.viewingId = this._state.processes[0]?.id || null;
    }
    this._write();
  }

  // ── Starter name (Fase 6) ────────────────────────────────
  setStarterName(name) {
    this._state.starterName = (name || '').trim().slice(0, 30);
    this._write();
  }

  getStarterName() {
    return this._state.starterName || '';
  }

  // ── Achievements (Fase 6) ─────────────────────────────────
  /**
   * Unlock an achievement by id.
   * Returns true if it was newly unlocked, false if already had it.
   */
  unlockAchievement(id) {
    if (!Array.isArray(this._state.achievements)) {
      this._state.achievements = [];
    }
    if (this._state.achievements.includes(id)) return false;
    this._state.achievements.push(id);
    this._write();
    return true;
  }

  getAchievements() {
    return Array.isArray(this._state.achievements) ? [...this._state.achievements] : [];
  }

  // ── Edit start date (reset cycle timeline) ───────────────
  /**
   * Override the start date of the active process.
   * @param {string} isoString — e.g. new Date(localDt).toISOString()
   * Returns the updated process or null if no active process.
   */
  setStartDate(isoString, processId) {
    const process = this.getProcess(processId);
    if (!process) return null;
    process.startDate  = isoString;
    process.currentDay = this._calcCurrentDay(isoString, process.type);
    this._write();
    return process;
  }

  // ── Clear all data (Fase 6) ───────────────────────────────
  clearAll() {
    this._state = structuredClone(DEFAULT_STATE);
    try {
      localStorage.removeItem(STORAGE_KEY);
      // Also clear onboarding flag so it shows again
      localStorage.removeItem('mm_onboarded');
    } catch (_) {}
  }

  // ── Helpers ───────────────────────────────────────────────

  _calcCurrentDay(startDateISO, type) {
    const maxDaysMap = { p1: 7, p2: 5, p3: 2 };
    const maxDays = maxDaysMap[type] ?? 7;
    const start   = new Date(startDateISO);
    const now     = new Date();
    const diffMs  = now - start;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
    return Math.min(Math.max(diffDays, 1), maxDays);
  }

  // Ms until next activity (based on timeOffset from startDate)
  msUntilNextActivity(activities) {
    const process = this.getViewingProcess();
    if (!process) return null;

    const start        = new Date(process.startDate);
    const completedIds = process.completedIds;
    const now          = Date.now();

    // Find the earliest incomplete activity whose scheduled time is in the future
    const pending = activities
      .filter(a => !completedIds.includes(a.id))
      .map(a => ({
        ...a,
        scheduledAt: start.getTime() + a.timeOffset * 60 * 60 * 1000,
      }))
      .sort((a, b) => a.scheduledAt - b.scheduledAt);

    // Past-due but incomplete: show 0
    const overdue = pending.find(a => a.scheduledAt <= now);
    if (overdue) return 0;

    // Next future activity
    const next = pending.find(a => a.scheduledAt > now);
    return next ? next.scheduledAt - now : null;
  }
}
